import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FileText, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TermsPage = () => {
  const { t } = useTranslation();

  const sections = [
    { title: t('terms.s1Title'),  content: t('terms.s1Content')  },
    { title: t('terms.s2Title'),  content: t('terms.s2Content')  },
    { title: t('terms.s3Title'),  content: t('terms.s3Content')  },
    { title: t('terms.s4Title'),  content: t('terms.s4Content')  },
    { title: t('terms.s5Title'),  content: t('terms.s5Content')  },
    { title: t('terms.s6Title'),  content: t('terms.s6Content')  },
    { title: t('terms.s7Title'),  content: t('terms.s7Content')  },
    { title: t('terms.s8Title'),  content: t('terms.s8Content')  },
    { title: t('terms.s9Title'),  content: t('terms.s9Content')  },
    { title: t('terms.s10Title'), content: t('terms.s10Content') },
    { title: t('terms.s11Title'), content: t('terms.s11Content') },
    { title: t('terms.s12Title'), content: t('terms.s12Content') },
    { title: t('terms.s13Title'), content: t('terms.s13Content') },
    { title: t('terms.s14Title'), content: t('terms.s14Content') },
    { title: t('terms.s15Title'), content: t('terms.s15Content') },
  ];

  return (
    <>
      <Helmet>
        <title>{t('terms.pageTitle')}</title>
        <meta name="description" content={t('terms.metaDesc')} />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">{t('terms.title')}</h1>
              <p className="text-xl text-gray-600 mb-4">{t('terms.subtitle')}</p>
              <p className="text-sm text-gray-500">{t('terms.lastUpdated')}</p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-12"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('terms.introTitle')}</h2>
                <p className="text-gray-700 leading-relaxed">{t('terms.introText')}</p>
              </motion.div>

              {/* Sections */}
              <div className="space-y-8">
                {sections.map((section, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm"
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                    <div
                      className="text-gray-700 leading-relaxed prose prose-primary max-w-none"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Contact CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-12 bg-gradient-primary text-white rounded-xl p-8 text-center"
              >
                <Mail className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <h3 className="text-2xl font-bold mb-2">{t('terms.ctaTitle')}</h3>
                <p className="text-blue-100 mb-6">{t('terms.ctaDesc')}</p>
                <Link to="/contact" className="btn btn-white">{t('terms.ctaBtn')}</Link>
              </motion.div>

              {/* Related Links */}
              <div className="mt-12 grid md:grid-cols-2 gap-6">
                <Link
                  to="/privacy"
                  className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all text-center"
                >
                  <h4 className="font-bold text-gray-900 mb-2">{t('terms.relPrivacyTitle')}</h4>
                  <p className="text-sm text-gray-600">{t('terms.relPrivacyDesc')}</p>
                </Link>
                <Link
                  to="/security"
                  className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all text-center"
                >
                  <h4 className="font-bold text-gray-900 mb-2">{t('terms.relSecurityTitle')}</h4>
                  <p className="text-sm text-gray-600">{t('terms.relSecurityDesc')}</p>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TermsPage;
